<template>
  <vx-card class="mt-base" :title="activityTitle">
    <p class="mb-5">
      A floor plan is a diagram of a room or a building drawn to scale and viewed from above. Floor
      plans can include the room’s dimensions and the locations of structural features such as doors
      and windows. Floor plans sometimes include furniture and other features too.
    </p>
    <p class="mb-5">
      Architects use floor plans to communicate to other professionals, technicians, and
      tradespeople how they visualise the room or building to look upon completion. Everyone
      involved in construction should be able to read floor plans.
    </p>
    <p class="mb-5">
      Floor plans are sometimes drawn by hand, but these days they’re more often designed using
      computer software.
    </p>
    <p class="mb-5">
      For this project, you’re going to draw a floor plan for your entire house using the free
      online floor planner software at
      <a href="https://www.floorplanner.com" target="_blank">www.floorplanner.com</a>
    </p>
    <p class="mb-5">Follow the link to the website above and then follow the instructions below:</p>

    <h4 class="mb-5">Create an Account</h4>
    <ol class="numbered-list">
      <li>Click the “Sign up for free” button</li>
      <li>
        Create a free account using one of the following options<br />
        <ul class="default-list">
          <li>Sign up using your Google account</li>
          <li>Sign up using your Facebook account</li>
          <li>Sign up using your Apple account</li>
          <li>Sign up using your email account</li>
        </ul>
      </li>
      <li>
        On the “Welcome on board” page complete your name, choose an avatar, select language, and
        set measurements to “metric”.
      </li>
      <li>Then, click “next” and choose the “for personal use” option. Hit “next” again</li>
      <li>Click “create project” and on the following page hit “room wizard”</li>
    </ol>
    <p class="mb-5 text-bold">Remember to SAVE your work as you go!</p>

    <h4 class="mb-5">Room Shape</h4>
    <p class="mb-5">
      Now, it’s time to choose a room to create a plan for using the editor. Your bedroom might be
      the best place to start. Play around with the room shape features until you’ve got a rough
      outline.
    </p>
    <p class="mb-5">When you're happy, click “Next”.</p>
    <h4 class="mb-5">Room Dimensions</h4>
    <p class="mb-5">
      Now, it’s time to set the dimension using the sliders on this page (or just type in the
      sizes). To do this, you’ll need to use your measuring skills.
    </p>
    <p class="mb-5">
      Take the necessary room measurements and write them down on a piece of paper. Then, transfer
      these measurements into the room planner programme.
    </p>
    <p class="mb-5">When you’ve done this, hit “Next”.</p>
    <h4 class="mb-5">Room Type</h4>
    <p class="mb-5">Next, select the room type and click “Next”.</p>
    <h4 class="mb-5">Add Features</h4>
    <p class="mb-5">
      On this page, you can add in the final touches such as windows, doors, etc. You can also move
      from an overhead 2D view to a 3D view as well as change the perspective to view the room from
      the inside.
    </p>
    <p class="mb-5">You can export the image too, to save a copy onto your computer.</p>
    <p class="mb-5">Now make a floor plan for 3 rooms in your home?</p>
    <h4 class="mb-5">Your Task:</h4>
    <p class="mb-5">
      Create a floorplan for 3 rooms in your house. To do this, you’ll need to take all the
      necessary measurements and make notes of where structural features such as windows and doors
      are.
    </p>
    <p class="mb-5">
      You can also add in fittings such as furniture to make it as close to the real room as
      possible.
    </p>
    <p class="mb-5">Again, remember to SAVE your work as you go!</p>
    <p class="mb-5">For each room, export the 2D or 3D image and you will upload these below:</p>

    <div v-for="index in 3" :key="index">
      <div class="overflow-hidden">
        <p class="float-left py-2 pr-2">
          <strong>Room {{ index }} Name: </strong>
        </p>
        <text-block
          v-model="data['question' + index]"
          :editable="isEditable"
          style="width: 300px; float: left"
          pclass="my-2"
        />
      </div>
      <p class="mb-5 text-bold">Upload 2D image</p>
      <fileupload-button-immediate
        v-model="uploaded_files['uploaded_file_' + index + '_1']"
        :is-editable="isEditable"
        filetype="photo_doc"
        :index="index + '_1'"
      />

      <p class="mb-5 text-bold">Upload 3D image</p>
      <fileupload-button-immediate
        v-model="uploaded_files['uploaded_file_' + index + '_2']"
        :is-editable="isEditable"
        filetype="photo_doc"
        :index="index + '_2'"
      />
    </div>
    <save-buttons :is-editable="isEditable" @save="save" />
  </vx-card>
</template>

<script>
import methods from '@/views/WorkExperiences/components/activity_js/methods';
import computed from '@/views/WorkExperiences/components/activity_js/computed';
import inject from '@/views/WorkExperiences/components/activity_js/inject';

export default {
  data() {
    return {
      uploaded_files: {
        uploaded_file_1_1: '',
        uploaded_file_1_2: '',
        uploaded_file_2_1: '',
        uploaded_file_2_2: '',
        uploaded_file_3_1: '',
        uploaded_file_3_2: '',
      },
    };
  },
  methods: methods,
  computed: computed,
  inject: inject,
  mounted() {
    if (typeof this.uploaded_files !== 'undefined') {
      for (let i = 1; i <= Object.keys(this.uploaded_files).length; i++) {
        for (let j = 1; j <= 2; j++) {
          if (typeof this.data['uploaded_file_' + i + '_' + j] !== 'undefined') {
            this.uploaded_files['uploaded_file_' + i + '_' + j] =
              this.data['uploaded_file_' + i + '_' + j];
          }
        }
      }
    }
  },
};
</script>

<style scoped>
.wrong {
  background: rgba(202, 20, 20, 0.24);
  border-bottom: 1px solid #f8f8f8;
  padding-top: 2px;
}
.correct {
  background: rgba(75, 202, 20, 0.24);
  border-bottom: 1px solid #f8f8f8;
}

.workplace_module {
  overflow: hidden;
  padding: 10px 0;
  border-bottom: 1px solid #dedede;
}
.workplace_module:last-child {
  border-bottom: none;
}
p.module_name {
  float: left;
  font-size: 16px;
  font-weight: 600;
  padding-top: 10px;
}
.prework_icon {
  float: right;
  width: 10%;
  text-align: center;
}
select {
  width: 100%;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 5px;
}
</style>
